import type { Transaction } from "@rocicorp/zero";
import * as z from "zod";
import { type EffortLevel, effortLevels } from "../aiProviders";
import { writeCommitFile } from "../commitFiles";
import { WORKFLOW_README_STUB } from "../readmeDescription";
import { DEFAULT_WORKFLOW_RETENTION_SECONDS } from "../executionRetention";
import { type Schema, zql } from "../generatedDrizzleSchema";
import {
  type ActorAttribution,
  actorColumns,
  type CreatorAttribution,
  creatorColumns,
} from "../rbac/roles";
import { stripNullBytes } from "../stripNullBytes";
import {
  DEFAULT_WORKFLOW_NAME,
  sanitizeWorkflowName,
  uniqueWorkflowName,
} from "../workflowName";
import {
  assertChatAttachmentsAllowed,
  attachmentSchema,
} from "./attachmentValidation";
import { mutator } from "./mutator";
import { clientGeneratableIdSchema } from "../id";

export const createWorkflowSchema = z.object({
  id: clientGeneratableIdSchema,
  spaceId: z.string(),
  branchId: clientGeneratableIdSchema,
  commitId: clientGeneratableIdSchema,
  chatId: clientGeneratableIdSchema,
  userEntryId: clientGeneratableIdSchema,
  name: z.string(),
  // Omitted when creating an empty workflow to build directly (no AI turn, no
  // initial user message); present when the AI should build from the prompt.
  prompt: z.string().transform(stripNullBytes).optional(),
  isDictated: z.boolean().optional(),
  planMode: z.boolean().optional(),
  effort: z.enum(effortLevels).optional(),
  chatModel: z.string().optional(),
  actionKind: z.enum(["sub_agent"]).optional(),
  attachments: z.array(attachmentSchema).optional(),
  clientSentAt: z.number().int().optional(),
  clientTimezone: z.string().optional(),
});

export type SkeletonAttachment = z.infer<typeof attachmentSchema>;

export async function insertWorkflowSkeleton(opts: {
  tx: Transaction<Schema>;
  creator: CreatorAttribution;
  actor: ActorAttribution;
  tenantId: string;
  workflowId: string;
  spaceId: string;
  branchId: string;
  commitId: string;
  chatId: string;
  userEntryId: string;
  name: string;
  prompt?: string;
  isDictated?: boolean;
  planMode?: boolean;
  effort?: EffortLevel;
  actionKind?: "sub_agent";
  attachments?: readonly SkeletonAttachment[];
}): Promise<void> {
  const {
    tx,
    creator,
    actor,
    tenantId,
    workflowId,
    spaceId,
    branchId,
    commitId,
    chatId,
    userEntryId,
    name,
    prompt,
    isDictated,
    planMode,
    effort,
    actionKind,
    attachments,
  } = opts;
  const now = Date.now();
  const hasMessage = !!prompt || (attachments?.length ?? 0) > 0;

  const trimmedName = name.trim();
  const needsName = trimmedName === "" || trimmedName === DEFAULT_WORKFLOW_NAME;
  const siblings = await tx.run(
    zql.workflows.where("spaceId", spaceId).where("deletedAt", "IS", null)
  );
  const finalName = uniqueWorkflowName(
    needsName ? DEFAULT_WORKFLOW_NAME : sanitizeWorkflowName(trimmedName),
    siblings.map((w) => w.name)
  );

  await tx.mutate.workflows.insert({
    id: workflowId,
    spaceId,
    name: finalName,
    needsName,
    createdAt: now,
    updatedAt: now,
    createdByUserId: creator.createdByUserId,
    createdByServiceAccountId: creator.createdByServiceAccountId,
    autofixEnabled: true,
    autoImproveEnabled: false,
    workflowLinkHidden: false,
    retentionSeconds: DEFAULT_WORKFLOW_RETENTION_SECONDS,
    draftVolumeSeed: "ask",
    tenantId,
  });

  await tx.mutate.spaces.update({
    id: spaceId,
    updatedAt: now,
  });

  await tx.mutate.workflowBranches.insert({
    id: branchId,
    workflowId,
    userId: actor.userId,
    serviceAccountId: actor.serviceAccountId,
    name: "First draft",
    headCommitId: commitId,
    createdAt: now,
    lastChatEntryAt: hasMessage ? now : null,
    tenantId,
  });

  if (actor.userId) {
    const [preferences] = await tx.run(
      zql.userPreferences.where("userId", actor.userId)
    );
    if (preferences?.aiProviderId) {
      await tx.mutate.branchModelPreferences.insert({
        branchId,
        userId: actor.userId,
        aiProviderId: preferences.aiProviderId,
        chatModel: preferences.chatModel ?? null,
        tenantId,
      });
    }
  }

  await tx.mutate.workflowCommits.insert({
    id: commitId,
    workflowId,
    branchId,
    parentCommitId: null,
    chatId,
    message: null,
    createdAt: now,
    createdByUserId: creator.createdByUserId,
    createdByServiceAccountId: creator.createdByServiceAccountId,
    tenantId,
  });

  await writeCommitFile(
    { tx, commitId, tenantId },
    "README.md",
    WORKFLOW_README_STUB
  );

  await tx.mutate.chats.insert({
    id: chatId,
    commitId,
    createdAt: now,
    lastChatEntryType: hasMessage ? "UserMessage" : null,
    respondingAt: hasMessage ? now : null,
    tenantId,
  });

  // An empty workflow has no opening message, no AI turn, and no contributor
  // yet — those appear when the builder first messages or edits.
  if (!hasMessage) return;

  await tx.mutate.chatEntries.insert({
    id: userEntryId,
    chatId,
    entryType: "UserMessage",
    createdAt: now,
    previousEntryId: null,
    tenantId,
  });
  await tx.mutate.userMessages.insert({
    entryId: userEntryId,
    userId: actor.userId,
    serviceAccountId: actor.serviceAccountId,
    text: prompt ?? "",
    isDictated: isDictated ?? null,
    planMode: planMode ?? null,
    effort: effort ?? null,
    actionKind: actionKind ?? null,
    tenantId,
  });
  // Contributors are the human facepile; a pure service account has no human to
  // show, so it records no contributor row.
  if (actor.userId) {
    await tx.mutate.branchContributors.insert({
      branchId,
      workflowId,
      userId: actor.userId,
      lastMessagedAt: now,
      tenantId,
    });
  }
  for (const att of attachments ?? []) {
    await tx.mutate.attachments.insert({
      id: att.id,
      entryId: userEntryId,
      fileName: att.fileName,
      mediaType: att.mediaType,
      sizeBytes: att.sizeBytes,
      tenantId,
    });
  }
}

export const createWorkflow = mutator({
  redactKeysOnSecret: ["prompt"],
  schema: createWorkflowSchema,
  resolve: {
    space: { kind: "space", from: "spaceId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.space.id,
    },
  ],
  fn: async ({
    tx,
    ctx,
    args: {
      id,
      space,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name,
      prompt,
      isDictated,
      planMode,
      effort,
      actionKind,
      attachments,
    },
  }) => {
    assertChatAttachmentsAllowed(attachments);
    await insertWorkflowSkeleton({
      tx,
      creator: creatorColumns(ctx),
      actor: actorColumns(ctx),
      tenantId: ctx.tenantId,
      workflowId: id,
      spaceId: space.id,
      branchId,
      commitId,
      chatId,
      userEntryId,
      name,
      prompt,
      isDictated,
      planMode,
      effort,
      actionKind,
      attachments,
    });
  },
});
